import React, { memo } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import EmptyJobsState from './EmptyJobsState';
import JobKindFilter from './JobKindFilter';
import JobsTable from './JobsTable';
import SelectionToolbar from './SelectionToolbar';
import StatusTabs from './StatusTabs';
import { JobRowHandlers } from './JobRow';
import { BusyAction } from './hooks/useJobActions';
import { SelectAllState } from './hooks/useJobSelection';
import { DashboardJob, JobCounts, JobKind, StatusFilter } from './jobModel';

export interface JobsPanelProps extends JobRowHandlers {
  loading: boolean;
  totalJobs: number;
  visibleJobs: DashboardJob[];
  counts: JobCounts;
  statusFilter: StatusFilter;
  kindFilter: ReadonlySet<JobKind>;
  selectedIds: ReadonlySet<string>;
  selectAllState: SelectAllState;
  downloadableSelectedCount: number;
  busy: Readonly<Record<string, BusyAction>>;
  bulkBusy: boolean;
  onStatusFilterChange: (value: StatusFilter) => void;
  onToggleKind: (kind: JobKind) => void;
  onResetFilters: () => void;
  onToggleAll: () => void;
  onClearSelection: () => void;
  onDownloadSelected: () => void;
  onDeleteSelected: () => void;
}

/** The jobs card: filter toolbar (or bulk-action bar while rows are selected) above the table. */
const JobsPanel: React.FC<JobsPanelProps> = ({
  loading,
  totalJobs,
  visibleJobs,
  counts,
  statusFilter,
  kindFilter,
  selectedIds,
  selectAllState,
  downloadableSelectedCount,
  busy,
  bulkBusy,
  onStatusFilterChange,
  onToggleKind,
  onResetFilters,
  onToggleAll,
  onClearSelection,
  onDownloadSelected,
  onDeleteSelected,
  ...rowHandlers
}) => {
  const hasSelection = selectedIds.size > 0;
  return (
    <Paper variant="outlined" component="section" aria-label="Jobs" sx={{ borderRadius: 4, overflow: 'hidden' }}>
      <Box
        sx={{
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pl: 1,
          pr: 2.5,
          flexWrap: 'wrap',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: hasSelection ? (theme) => alpha(theme.palette.primary.main, 0.12) : 'transparent',
        }}
      >
        {hasSelection ? (
          <SelectionToolbar
            count={selectedIds.size}
            downloadableCount={downloadableSelectedCount}
            busy={bulkBusy}
            onClear={onClearSelection}
            onDownload={onDownloadSelected}
            onDelete={onDeleteSelected}
          />
        ) : (
          <>
            <StatusTabs value={statusFilter} counts={counts.byStatus} onChange={onStatusFilterChange} />
            <Box sx={{ ml: 'auto', py: 1.25 }}>
              <JobKindFilter selected={kindFilter} counts={counts.byKind} onToggle={onToggleKind} />
            </Box>
          </>
        )}
      </Box>
      {loading ? <LinearProgress aria-label="Loading jobs" /> : null}
      <JobsTable
        jobs={visibleJobs}
        selectedIds={selectedIds}
        busy={busy}
        selectAllState={selectAllState}
        onToggleAll={onToggleAll}
        {...rowHandlers}
      />
      {!loading && visibleJobs.length === 0 ? <EmptyJobsState filtered={totalJobs > 0} onResetFilters={onResetFilters} /> : null}
    </Paper>
  );
};

export default memo(JobsPanel);
